import { TermUnit } from 'src/app/pricing/models';

/**
 * Calculate remaining trial days using duration and optional term unit
 * 
 * @param startDate - Trial start date
 * @param duration - Duration amount (e.g., 2 for "2 weeks")
 * @param termUnit - Optional term unit (DAY/WEEK/MONTH/YEAR)
 * @param returnAsNumber - Whether to return number instead of formatted string
 * @returns Remaining days as string or number
 */
export function calculateTrialDaysFromDuration(
    startDate: Date | string,
    duration: number,
    termUnit?: string,
    returnAsNumber: boolean = false
): string | number {
    const start = new Date(startDate);
    const now = new Date();

    // Convert duration to days based on term unit
    const durationInDays = convertToDays(duration, termUnit);

    // Calculate elapsed days since trial start
    const elapsedMs = now.getTime() - start.getTime();
    const elapsedDays = Math.floor(elapsedMs / (1000 * 60 * 60 * 24));

    // Calculate remaining days
    const remainingDays = durationInDays - elapsedDays;

    return formatTrialResult(remainingDays, returnAsNumber);
}

/**
 * Calculate remaining trial days using end date
 *
 * @param endDate - Trial end date
 * @param returnAsNumber - Whether to return number instead of formatted string
 * @returns Remaining days as string or number
 */
export function calculateTrialDaysFromEndDate(
    endDate: Date | string,
    returnAsNumber: boolean = false
): string | number {
    const end = new Date(endDate);
    const now = new Date();

    const startOfDay = (d: Date) => {
        const copy = new Date(d);
        copy.setHours(0, 0, 0, 0);
        return copy;
    };
    const endDay = startOfDay(end);
    const today = startOfDay(now);
    const dayMs = 1000 * 60 * 60 * 24;
    const remainingDays = Math.round((endDay.getTime() - today.getTime()) / dayMs);

    return formatTrialResult(remainingDays, returnAsNumber);
}

/**
 * Calculate trial days left - supports both duration and end date
 * 
 * Usage:
 * - calculateTrialDaysLeft(startDate, endDate) // With end date
 * - calculateTrialDaysLeft(startDate, 30) // With duration in days
 * - calculateTrialDaysLeft(startDate, 2, 'WEEK') // With duration and term
 * - calculateTrialDaysLeft(startDate, 30, undefined, true) // Return as number
 * 
 * @param startDate - Trial start date
 * @param durationOrEndDate - Either duration amount (number) or trial end date (Date/string)
 * @param termUnit - Optional term unit (DAY/WEEK/MONTH/YEAR) when using duration
 * @param returnAsNumber - Whether to return number instead of formatted string
 * @returns Remaining days as string or number
 */
export function calculateTrialDaysLeft(
    startDate: Date | string,
    durationOrEndDate: number | Date | string,
    termUnit?: string,
    returnAsNumber: boolean = false
): string | number {
    if (!startDate || !durationOrEndDate) {
        return returnAsNumber ? 0 : 'N/A';
    }

    // Check if durationOrEndDate is a number (duration) or date (end date)
    if (typeof durationOrEndDate === 'number') {
        return calculateTrialDaysFromDuration(startDate, durationOrEndDate, termUnit, returnAsNumber);
    } else {
        return calculateTrialDaysFromEndDate(durationOrEndDate, returnAsNumber);
    }
}

/**
 * Convert duration to days based on term unit
 * 
 * @param duration - Duration amount
 * @param termUnit - Term unit (DAY/WEEK/MONTH/YEAR)
 * @returns Duration in days
 */
export function convertToDays(duration: number, termUnit?: string): number {
    if (!termUnit) {
        return duration; // Default to days
    }

    const unit = termUnit.toUpperCase();

    switch (unit) {
        case 'DAY':
        case 'DAYS':
        case TermUnit.DAY:
            return duration;

        case 'WEEK':
        case 'WEEKS':
        case TermUnit.WEEK:
            return duration * 7;

        case 'MONTH':
        case 'MONTHS':
        case TermUnit.MONTH:
            return duration * 30; // Approximate

        case 'YEAR':
        case 'YEARS':
        case TermUnit.YEAR:
            return duration * 365; // Approximate

        default:
            return duration; // Default to days
    }
}

/**
 * Format the result based on remaining days and return type
 * 
 * @param remainingDays - Number of remaining days
 * @param returnAsNumber - Whether to return number instead of formatted string
 * @returns Formatted result as string or number
 */
function formatTrialResult(remainingDays: number, returnAsNumber: boolean): string | number {
    if (remainingDays <= 0) {
        return returnAsNumber ? 0 : 'Trial expired';
    }

    if (returnAsNumber) {
        return remainingDays;
    }

    return `${remainingDays} day${remainingDays > 1 ? 's' : ''}`;
}
